import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { AppUpdate, AppUpdateAvailability } from '@capawesome/capacitor-app-update';
import { ModalController } from '@ionic/angular';
import { AppUpdateModalComponent } from '../../modals/app-update-modal/app-update-modal.component';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AppUpdateService {
  private isModalOpen = false;

  constructor(private modalController: ModalController) { }

  // To test locally, uncomment the two lines below.
  // The in-app update API only works with Play Store / App Store installs.
  // This will show the modal with the fallback "open store" flow on "Update Now".
  // TODO: Remove comment when tested with app store builds successfully.
  async checkForUpdate(): Promise<void> {
    // await this.showUpdateModal();
    // return;

    if (Capacitor.getPlatform() === 'web' || this.isModalOpen) {
      return;
    }

    try {
      const result = await AppUpdate.getAppUpdateInfo();

      if (result.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE) {
        await this.showUpdateModal();
      }
    } catch (error) {
      console.error('App update check failed:', error);
    }
  }

  private async showUpdateModal(): Promise<void> {
    this.isModalOpen = true;

    const modal = await this.modalController.create({
      component: AppUpdateModalComponent,
      cssClass: 'appUpdateModal',
      backdropDismiss: false,
    });

    modal.onDidDismiss().then(async (result) => {
      this.isModalOpen = false;
      if (result.data === true) {
        await this.handleUpdate();
      }
    });

    await modal.present();
  }

  private async handleUpdate(): Promise<void> {
    if (Capacitor.getPlatform() === 'android') {
      try {
        const result = await AppUpdate.getAppUpdateInfo();
        if (result.immediateUpdateAllowed) {
          await AppUpdate.performImmediateUpdate();
          return;
        }
      } catch (error) {
        console.error('Android in-app update failed:', error);
      }
    }
    // iOS or Android fallback: open the store
    await AppUpdate.openAppStore();
  }

  getCurrentAppVersion = async () => {
    const result = await AppUpdate.getAppUpdateInfo();
    if (Capacitor.getPlatform() === 'android') {
      return result.currentVersionCode;
    } else {
      return result.currentVersionName;
    }
  };

  getAvailableAppVersion = async () => {
    const result = await AppUpdate.getAppUpdateInfo();
    if (Capacitor.getPlatform() === 'android') {
      return result.availableVersionCode;
    } else {
      return result.availableVersionName;
    }
  };

  openAppStore = async () => {
    await AppUpdate.openAppStore();
  };

  performImmediateUpdate = async () => {
    const result = await AppUpdate.getAppUpdateInfo();
    if (result.updateAvailability !== AppUpdateAvailability.UPDATE_AVAILABLE) {
      return;
    }
    if (result.immediateUpdateAllowed) {
      await AppUpdate.performImmediateUpdate();
    }
  };

  startFlexibleUpdate = async () => {
    const result = await AppUpdate.getAppUpdateInfo();
    if (result.updateAvailability !== AppUpdateAvailability.UPDATE_AVAILABLE) {
      return;
    }
    if (result.flexibleUpdateAllowed) {
      await AppUpdate.startFlexibleUpdate();
    }
  };

  completeFlexibleUpdate = async () => {
    await AppUpdate.completeFlexibleUpdate();
  };
}
